import { z } from 'zod';
import { apiClientResp } from '../merchant/error.js';
import { calculatedOrderNoResolutions, calculatedOrderShipmentQuote, } from '../schema/calculatedOrder.js';
import { CrewUi } from './schema.js';
//* URL params
const settingsParams = z.object({
    slug: z.string(),
});
const storefrontIdParams = z.object({
    storefrontId: z.string(),
});
const storeIdProductParams = z
    .object({
    idFromPlatform: z.string(),
})
    .merge(storefrontIdParams);
const claimIdParams = z
    .object({ claimExternalId: z.coerce.string() })
    .merge(storefrontIdParams);
const gspClaimIdParams = z
    .object({ claimId: z.coerce.number() })
    .merge(storefrontIdParams);
const storeIdFromPlatformQuery = z.object({
    idFromPlatform: z.string(),
});
const storeIdOrderNoQuery = z.object({
    orderNo: z.string(),
});
//* URL queries
const storeIdOrderQueryWithEmailOrPostalCode = z
    .object({
    emailOrPostalCode: z.string(),
})
    .merge(storeIdOrderNoQuery);
const storeIdOrderUnionQuery = z.union([
    storeIdFromPlatformQuery,
    storeIdOrderQueryWithEmailOrPostalCode,
]);
const signedUrlQuery = z.object({
    filename: z.string(),
    contentType: z.string(),
    destination: z.union([
        z.literal('gspClaimImg'),
        z.literal('crewClaimImg'),
        z.literal('crewProofOfPurchase'),
    ]),
});
//* URL bodies
// ------- URL paths -----------------------------
const shippingOrderPath = `/protection/order`;
const settingsPath = '/settings/:slug';
// ------- everything below here is namespaced by the storefrontId and GSP or CREW where the need to disambiguate exists
const storefrontIdPath = '/:storefrontId';
const extensionHeartbeatPath = `${storefrontIdPath}/extension/heartbeat`;
const registrationPath = `${storefrontIdPath}/registration`;
const registrationProductPath = `${registrationPath}/product`;
const automationsPath = `${storefrontIdPath}/automations`;
const automationsAfterReasonSelectionPath = `${automationsPath}/after-reason-selection`;
const createPaymentIntentPath = `${storefrontIdPath}/create-payment-intent`;
const signedUrlPath = `${storefrontIdPath}/signed-url`;
const storeIdOrderPath = `${storefrontIdPath}/order`;
const storeIdProductPath = `${storefrontIdPath}/product/:idFromPlatform`;
const crewClaimPath = `${storefrontIdPath}/claim`;
const crewClaimIdPath = `${crewClaimPath}/:claimExternalId`;
const shippingClaimPath = `${storefrontIdPath}/shipping-protection/claim`;
const shippingClaimIdPath = `${shippingClaimPath}/:shippingClaimId`;
const claimReturnShipmentPath = `${storefrontIdPath}/return-shipment`;
const claimReturnShipmentWhPath = `${claimReturnShipmentPath}/wh`;
// ? quote paths
const claimReturnShipmentQuotePath = `${claimReturnShipmentPath}/quote`;
// order calculate path
const storeIdOrderCalculatePath = `${storeIdOrderPath}/calculate`;
const storeOrderCalculatedReasonSelection = `${storeIdOrderCalculatePath}/after-reason-selection`;
const storeOrderCalculateBeforeClaimSubmission = `${storeIdOrderCalculatePath}/before-claim-submission`;
const storeOrderCalculatedEstimatedIncentive = `${storeIdOrderCalculatePath}/estimate-incentive`;
//! all endpoints exposed by the crewUiApi are at a base path of `/v1/shop/crew/`
//! storefrontId is returned in the call to settings, and all subsequent calls require it
/**
 * crewUiApi object containing the following:
 * 1. A list of all routes exposed for consumption by the crew-ui (all routes are prefixed with `/v1/shop/crew/`).
 * 2. All query params, path params, and request/response bodies for each route.
 */
export const crewCustomerApi = {
    // load for GSP flow
    [shippingOrderPath]: {
        path: shippingOrderPath,
        get: {
            request: {
                query: storeIdOrderUnionQuery,
            },
            response: { body: CrewUi.gspOrderSchema },
        },
    },
    // load for CREW flow
    [settingsPath]: {
        path: settingsPath,
        get: {
            request: { params: settingsParams },
            response: { body: CrewUi.settingsSchema },
        },
    },
    [extensionHeartbeatPath]: {
        path: extensionHeartbeatPath,
        get: {
            request: {
                params: storefrontIdParams,
                query: z.object({
                    customerAppUrl: z.string().optional(),
                    embeddedHostUrl: z.string().optional(),
                    appName: z.string().optional(),
                }),
            },
        },
    },
    // new calculated order paths
    [storeOrderCalculatedReasonSelection]: {
        path: storeOrderCalculatedReasonSelection,
        post: {
            request: {
                params: storefrontIdParams,
                body: calculatedOrderNoResolutions,
            },
            response: {
                body: CrewUi.calculatedAfterReasonSelectionSchema,
            },
        },
    },
    [storeOrderCalculatedEstimatedIncentive]: {
        path: storeOrderCalculatedEstimatedIncentive,
        post: {
            request: {
                params: storefrontIdParams,
                body: CrewUi.calculatedOrderSchema,
            },
            response: {
                body: CrewUi.calculatedIncentiveEstimateSchema,
            },
        },
    },
    [storeOrderCalculateBeforeClaimSubmission]: {
        path: storeOrderCalculateBeforeClaimSubmission,
        post: {
            request: {
                params: storefrontIdParams,
                body: CrewUi.calculatedOrderSchema,
            },
            response: {
                body: CrewUi.calculatedBeforeClaimSubmissionSchema,
            },
        },
    },
    [registrationPath]: {
        path: registrationPath,
        post: {
            request: {
                params: storefrontIdParams,
                body: CrewUi.registrationCreateSchema,
            },
            response: { body: CrewUi.registrationSchema },
        },
    },
    [automationsAfterReasonSelectionPath]: {
        path: automationsAfterReasonSelectionPath,
        post: {
            request: {
                params: storefrontIdParams,
                query: z.object({
                    originalStoreOrderLineItemId: z.coerce.number(),
                }),
            },
            response: {
                body: apiClientResp,
            },
        },
    },
    [registrationProductPath]: {
        path: registrationProductPath,
        get: {
            request: {
                params: storefrontIdParams,
                query: z.object({
                    storeSalesChannelName: z.string(),
                    estimatedPurchaseDate: z.string(),
                    searchTerm: z.string(),
                }),
            },
            response: { body: z.array(CrewUi.registrationProductSchema) },
        },
    },
    [shippingClaimPath]: {
        path: shippingClaimPath,
        post: {
            request: {
                params: storefrontIdParams,
                body: CrewUi.shippingClaimCreateCustomerSchema,
            },
            response: { body: CrewUi.shippingClaimSchema },
        },
    },
    [crewClaimPath]: {
        path: crewClaimPath,
        post: {
            request: { params: storefrontIdParams, body: CrewUi.claimCreateSchema },
            response: { body: CrewUi.claimSchema },
        },
    },
    [storeIdOrderPath]: {
        path: storeIdOrderPath,
        get: {
            request: {
                params: storefrontIdParams,
                query: storeIdOrderUnionQuery,
            },
            response: { body: CrewUi.orderSchema },
        },
    },
    [crewClaimIdPath]: {
        path: crewClaimIdPath,
        get: {
            request: { params: claimIdParams },
            response: { body: CrewUi.claimSchema },
        },
    },
    [shippingClaimIdPath]: {
        path: shippingClaimIdPath,
        get: {
            request: { params: gspClaimIdParams },
            response: { body: CrewUi.shippingClaimSchema },
        },
    },
    [storeIdProductPath]: {
        path: storeIdProductPath,
        get: {
            request: { params: storeIdProductParams },
            response: { body: CrewUi.variantExchangeSchema },
        },
    },
    [claimReturnShipmentPath]: {
        path: claimReturnShipmentPath,
        post: {
            request: {
                params: storefrontIdParams,
                body: CrewUi.shipmentCreateSchema,
            },
            response: { body: CrewUi.shipmentSchema.nullable() },
        },
    },
    [claimReturnShipmentQuotePath]: {
        path: claimReturnShipmentQuotePath,
        post: {
            request: {
                params: storefrontIdParams,
                body: calculatedOrderShipmentQuote,
            },
            response: { body: CrewUi.shipmentQuoteSchema },
        },
    },
    [claimReturnShipmentWhPath]: {
        path: claimReturnShipmentWhPath,
        // WH path for easy post updates
        post: { request: { params: storefrontIdParams } },
    },
    [signedUrlPath]: {
        path: signedUrlPath,
        get: {
            request: { params: storefrontIdParams, query: signedUrlQuery },
            response: { body: CrewUi.signedUrlSchema },
        },
    },
    [createPaymentIntentPath]: {
        path: createPaymentIntentPath,
        post: {
            request: {
                params: storefrontIdParams,
                body: CrewUi.paymentIntentCreateSchema,
            },
            response: { body: CrewUi.paymentIntentSchema },
        },
    },
};
//# sourceMappingURL=api.js.map